import { Form, Formik, FormikHelpers } from "formik";
import { ReturnInventoryType, TransferInventoryType } from "../../models/inventory";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPresignedURL } from "../../api/presignedURL";
import { uploadWithPresignedUrl } from "../../libs/function";
import { PageTitle } from "../../../_metronic/layout/core";
import { inventoryMoveSchema, inventoryReturnSchema } from "../../schemas/inventory";
import { FormBody } from "../../components/form/FormBody";
import { FormInputAsyncSearch, FormInputDate, FormInputFile, FormInputRadio, InputDate } from "../../components/form/FormInputs";
import { FormFooterBase } from "../../components/form/FormFooters";
import { useGetParentOU } from "../../hooks/ou";
import { OUType } from "../../models/ou";
import { OptionType } from "../../models/base";
import { useTransferInventoryBatch } from "../../hooks/inventory";
import { useAppContext } from "../../../_metronic/helpers/AppContext";

const MoveInventoryPage = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const {mutate: transferInventory} = useTransferInventoryBatch()
  const navigate = useNavigate();
  const { data: dataOU } = useGetParentOU();
  const [optionsOU, setOptionsOU] = useState<OptionType[]>([]);
  const [action, setAction] = useState<string>('return');
  const { role } = useAppContext();
  const formInitValue = {
    organization_source_id : '',
    organization_target_id : '',
    action : '',
    return_date: '',
    start_rent: '',
    end_rent: '',
    file_url: ''
  }

  const [isFormComplete, setIsFormComplete] = useState<boolean>(false);
  const [formValue, setFormValue] = useState(formInitValue);

  useEffect(() => {
    if(dataOU) {
      const dataOptions : OptionType[] = dataOU
      .filter((ou: OUType) => ou.is_active === true)
      .map((ou: OUType) => ({
        value: ou.id,
        label: ou.name,
      }));
      setOptionsOU(dataOptions);
    }
  },[dataOU])

  useEffect(() => {
    if(action === 'return') {
      if(formValue.organization_source_id !== '' && selectedFile !== null) {
        setIsFormComplete(true);
      } else {
        setIsFormComplete(false);
      }
    } else if (action === 'transfer') {
      if(formValue.organization_target_id !== '' && formValue.start_rent !== '' && formValue.end_rent !== '' && selectedFile !== null) {
        setIsFormComplete(true);
      } else {
        setIsFormComplete(false);
      }
    }
  },[formValue , selectedFile, action])




  const onSubmit = async(values: TransferInventoryType | ReturnInventoryType, action: FormikHelpers<TransferInventoryType | ReturnInventoryType>) => {
    console.log(values);
    if (values.action === 'return') {
      onSubmitReturn(values as ReturnInventoryType, action as FormikHelpers<ReturnInventoryType>);
    } else if (values.action === 'transfer') {
      onSubmitMove(values as TransferInventoryType, action as FormikHelpers<TransferInventoryType>);
      
    }
  }

  const onSubmitMove = async(values: TransferInventoryType , action: FormikHelpers<TransferInventoryType>) => {
    setIsUploading(true);
    const rootFolder = process.env.REACT_APP_AWS_S3_FOLDER;
    const fileExtension = selectedFile ? selectedFile.name.split('.').pop()?.toLowerCase() : 'xlsx';
        const fileName = `${rootFolder}/import_logs/inventory-transfer/Upload.${fileExtension}`;
        try {
          const presignedURL = await getPresignedURL(fileName);
          const url = presignedURL.data.split('.com')[0] + '.com'; 
          await uploadWithPresignedUrl(selectedFile, presignedURL.data );
          const fileUrl = (`${url}/${fileName}`);
          delete values.file;
          const mappedValues = {
            organization_target_id : values.organization_target_id,
            action : values.action,
            start_rent: values.start_rent,
            end_rent: values.end_rent,
            file_url: fileUrl
          };
          transferInventory({ data : mappedValues });
          setIsUploading(false);
        } catch (error) {
          console.log(error);
          setIsUploading(false);
        } 
        action.resetForm();
        navigate('/inventory');
  }

  const onSubmitReturn = async(values: ReturnInventoryType , action: FormikHelpers<ReturnInventoryType>) => {
    setIsUploading(true);
    const rootFolder = process.env.REACT_APP_AWS_S3_FOLDER;
    const fileExtension = selectedFile ? selectedFile.name.split('.').pop()?.toLowerCase() : 'xlsx';
        const fileName = `${rootFolder}/import_logs/inventory-return/Upload.${fileExtension}`;
        try {
          const presignedURL = await getPresignedURL(fileName);
          const url = presignedURL.data.split('.com')[0] + '.com'; 
          await uploadWithPresignedUrl(selectedFile, presignedURL.data );
          const fileUrl = (`${url}/${fileName}`);
          delete values.file;
          const mappedValues = {
            organization_source_id : values.organization_source_id,
            action : values.action,
            return_date: values.return_date,
            file_url: fileUrl
          };
          transferInventory({ data : mappedValues });
          setIsUploading(false);
        } catch (error) {
          console.log(error);
          setIsUploading(false);
        }
        action.resetForm();
        navigate('/inventory');
  }
  const mode = process.env.REACT_APP_ENVIRONMENT;
  if (role !== 1 || mode === "production") {
    return (
      <>
      </>
    )
  }

  return (
    <>
    <PageTitle breadcrumbs={[{
    title: 'TSM',
    path: '/inventory',
    description:'Pindahkan Perangkat',
    isActive: true,
    isSeparator: false
    }]}>Inventory</PageTitle>

    <div className="card mb-5 mx-xl-10">
      <Formik
        initialValues={formInitValue}
        validationSchema={action === 'return' ? inventoryReturnSchema : inventoryMoveSchema} 
        onSubmit={onSubmit}
      >
        <Form>
          <FormBody>
            <h2 className="fs-3 fw-bold text-gray-800 mb-3">Pindahkan Perangkat</h2>
            <hr className="mb-6  text-gray-400" />
            <FormInputRadio
              name='action'
              labelName='Proses Pemindahan'
              required={true}
              values={['return','transfer']}
              valuesLabel={['Pengembalian Perangkat','Pindahkan Perangkat']}
              checkedValue="return"
              onSelectedValueChange={(value) => {
                setAction(value)
                setFormValue({...formValue, action: value})
              }}
            />
            <FormInputAsyncSearch
              name="organization_source_id"
              labelName="Sumber Perangkat"
              label={action !== 'return' ? 'TSM' : 'Pilih'}
              value={action !== 'return' ? 'TSM' : undefined}
              disabled={action !== 'return'}
              options={optionsOU}
              onSelectedValue={(value) => setFormValue({...formValue, organization_source_id: value as string})}
            />
            <FormInputAsyncSearch
              name="organization_target_id"
              labelName="Target Unit Organisasi"
              label={action === 'return' ? 'TSM' : 'Pilih'}
              value={action === 'return' ? 'TSM' : undefined}
              disabled={action === 'return'}
              options={optionsOU}
              onSelectedValue={(value) => setFormValue({...formValue, organization_target_id: value as string})}
            />
            {action === 'return' && (
              <FormInputDate
              name="return_date"
              labelName="Tanggal Pengembalian"
              disabled={true}
              onValueChange={(value) => setFormValue({...formValue, return_date: value as string})}
            />)}
            {action === 'transfer' && (
            <div className="row">
              <div className="col-4">
                <label htmlFor='start_rent' className='col-lg-12 col-form-label fw-bold fs-6'>
                  <span className='required'>Tanggal Sewa</span>
                </label>
              </div>
              <div className="col-3">
                <InputDate labelName="Pilih Tanggal" value={''} name="start_rent" required  
                onValueChange={(value) => setFormValue({...formValue, start_rent: value as string})}/>
              </div>
              <div className="col-2">
                <label htmlFor='end_rent' className='col-lg-12 col-form-label fw-bold fs-6 text-left'>
                  <span className='required'>Sampai</span>
                </label>
              </div>
              <div className="col-3">
                <InputDate labelName="Pilih Tanggal" name="end_rent" value={''} required
                onValueChange={(value) => setFormValue({...formValue, end_rent: value as string})}/>
              </div>
            </div>)}


            <FormInputFile name="file_url" labelName="File" meta={{ error: 'File', touched: false }} typeInput="transfer-inventory" onValueChange={(e) => {
              setSelectedFile(e);
              setFormValue({...formValue, file_url: e? e.name : ''});
            }}/>
          </FormBody>
          <FormFooterBase linkTo={"/inventory"} confirmText={isUploading ? 'Loading...' : 'Simpan'} isloading={isUploading} confirmDisable={!isFormComplete} />
        </Form>
      </Formik>

    </div>
  </>
  )
}

export default MoveInventoryPage